/**
 * Help Docs page — per docs/specs/help-docs.md
 *
 * 122-doc pipeline: regenerate, search analytics, stale detection.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-22-v6
 */
import { useEffect, useRef, useState } from 'react';
import { RefreshCw, Save } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Chip } from '../components/chip';
import { Modal } from '../components/modal';
import { RoleButton } from '../components/role-gate';
import { useToast } from '../components/toast';
import { appendAuditEntry } from '../../lib/mutations';
import { useAdminSession } from '../../lib/auth';
import { useFailedQueries, useHelpDocs, useHelpDocsKpis, useTopQueries } from '../../lib/api';
import { apiPost, getHttpErrorMessage } from '../../lib/http';
import type { HelpDocGenerationRun, HelpDocRow } from '../../lib/types';
import { HelpDocsRegenConfirm } from './help-docs-regen-confirm';

type PipelineStatus = 'idle' | 'running' | 'success' | 'failed';
type PipelineOperation = 'none' | 'regen' | 'reindex';
type RegenScope =
    | { kind: 'single'; slug: string; title: string }
    | { kind: 'all'; total: number }
    | { kind: 'stale'; slugs: string[]; total: number };
type PublishState = 'draft' | 'needs_review' | 'published';
type LocalDocDraft = {
    title: string;
    publishState: PublishState;
    reviewNotes: string;
    bodyMarkdown: string;
};
type PipelineRun = {
    status: PipelineStatus;
    operation: PipelineOperation;
    scopeLabel: string;
    docsAffected: number;
    lastStartedAt: string | null;
    lastDurationMs: number | null;
    lastMessage: string | null;
    lastCompletedAt: string | null;
};

const PIPELINE_RUN_LABELS: Record<PipelineOperation, string> = {
    none: 'Idle',
    regen: 'Regeneration',
    reindex: 'Index rebuild'
};

const publishStateTone: Record<PublishState, 'success' | 'warning' | 'muted'> = {
    published: 'success',
    needs_review: 'warning',
    draft: 'muted'
};

const publishStateLabel: Record<PublishState, string> = {
    published: 'Published',
    needs_review: 'Needs review',
    draft: 'Draft'
};

function fmtPipelineChip(status: PipelineStatus, op: PipelineOperation) {
    if (status === 'running') return 'info';
    if (status === 'failed') return 'danger';
    if (status === 'success') return 'success';
    return op === 'reindex' ? 'success' : 'muted';
}

function scopeLabel(scope: RegenScope) {
    if (scope.kind === 'single') return `single ${scope.title}`;
    if (scope.kind === 'all') return `all (${scope.total})`;
    return `stale (${scope.total})`;
}

function runtimeLabel(docsAffected: number) {
    if (docsAffected <= 1) return '~10 sec';
    if (docsAffected <= 20) return `~${Math.max(1, Math.round(docsAffected * 0.4))} min`;
    return '~18 min';
}

function latestRunLabel(run?: HelpDocGenerationRun | null) {
    if (!run) return 'No durable run history yet';
    const cost = (run.cost_cents / 100).toFixed(run.cost_cents % 100 === 0 ? 0 : 2);
    return `${run.operation} · ${run.status} · $${cost} · ${run.provider} · fallback ${run.fallback_provider}`;
}

export function HelpDocsPage() {
    const kpis = useHelpDocsKpis();
    const docs = useHelpDocs();
    const top = useTopQueries();
    const failed = useFailedQueries();
    const qc = useQueryClient();
    const { toast } = useToast();
    const session = useAdminSession();

    const [regenScope, setRegenScope] = useState<RegenScope | null>(null);
    const [pipelineRun, setPipelineRun] = useState<PipelineRun>({
        status: 'idle',
        operation: 'none',
        scopeLabel: 'No run yet',
        docsAffected: 0,
        lastStartedAt: null,
        lastDurationMs: null,
        lastMessage: null,
        lastCompletedAt: null
    });
    const [previewDoc, setPreviewDoc] = useState<HelpDocRow | null>(null);
    const [localDrafts, setLocalDrafts] = useState<Record<string, LocalDocDraft>>({});
    const runTimerRef = useRef<number | null>(null);

    const staleDocs = docs.data?.filter((d) => d.stale) ?? [];
    const staleCount = staleDocs.length;
    const isRunning = pipelineRun.status === 'running';
    const latestRun = kpis.data?.recent_runs?.[0] ?? null;

    useEffect(() => {
        return () => {
            if (runTimerRef.current) clearTimeout(runTimerRef.current);
        };
    }, []);

    useEffect(() => {
        if (!latestRun || isRunning) return;

        setPipelineRun({
            status: latestRun.status === 'failed' ? 'failed' : 'success',
            operation: latestRun.operation === 'reindex' ? 'reindex' : 'regen',
            scopeLabel: `${latestRun.scope} · ${latestRun.docs_affected} docs`,
            docsAffected: latestRun.docs_affected,
            lastStartedAt: latestRun.created_at ?? null,
            lastDurationMs: latestRun.duration_ms,
            lastMessage: latestRun.message,
            lastCompletedAt: latestRun.completed_at ?? latestRun.created_at ?? null
        });
    }, [isRunning, latestRun]);

    const runPipeline = async (
        operation: PipelineOperation,
        scope: string,
        docsAffected: number,
        detail: string,
        requestBody: Record<string, unknown>
    ) => {
        const started = new Date().toLocaleTimeString();
        if (runTimerRef.current) clearTimeout(runTimerRef.current);

        setPipelineRun({
            status: 'running',
            operation,
            scopeLabel: scope,
            docsAffected,
            lastStartedAt: started,
            lastDurationMs: null,
            lastMessage: null,
            lastCompletedAt: null
        });

        const endpoint =
            operation === 'reindex'
                ? '/api/admin/help-docs/reindex'
                : '/api/admin/help-docs/regenerate';

        try {
            await apiPost(endpoint, requestBody);
            const baseMs = operation === 'reindex' ? 1200 : 950;
            const targetMs =
                operation === 'regen' ? Math.min(2600, baseMs + docsAffected * 35) : baseMs;

            runTimerRef.current = window.setTimeout(() => {
                const completed = new Date().toLocaleTimeString();
                setPipelineRun((prev) => ({
                    ...prev,
                    status: 'success',
                    operation: 'none',
                    lastCompletedAt: completed,
                    lastDurationMs: targetMs,
                    lastMessage: detail
                }));
                void Promise.all([
                    qc.invalidateQueries({ queryKey: ['admin', 'help-docs', 'kpis'] }),
                    qc.invalidateQueries({ queryKey: ['admin', 'help-docs', 'list'] })
                ]);
                appendAuditEntry(qc, {
                    actor: session.email,
                    actor_role: session.role,
                    action: operation === 'reindex' ? 'help_docs.reindex' : 'help_docs.regenerate',
                    target_type: 'help_doc',
                    target_id: scope,
                    severity: 'routine',
                    reason: `${operation} requested from admin`
                });
                toast(
                    operation === 'reindex'
                        ? 'Search index rebuild finished'
                        : `${scope} regeneration finished`,
                    {
                        kind: 'success',
                        detail:
                            operation === 'reindex'
                                ? 'worker mutation completed'
                                : `${docsAffected} docs`
                    }
                );
            }, targetMs);
        } catch (error) {
            setPipelineRun((prev) => ({
                ...prev,
                status: 'failed',
                operation: 'none',
                lastCompletedAt: new Date().toLocaleTimeString(),
                lastDurationMs: null,
                lastMessage: getHttpErrorMessage(error, 'Worker mutation failed')
            }));
            toast('Help docs pipeline failed', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Worker mutation failed')
            });
        }
    };

    const handleConfirmRegen = (scope: RegenScope) => {
        const docsAffected = scope.kind === 'single' ? 1 : scope.total;
        void runPipeline('regen', scopeLabel(scope), docsAffected, runtimeLabel(docsAffected), {
            scope: scope.kind,
            slugs:
                scope.kind === 'single' ? [scope.slug] : scope.kind === 'stale' ? scope.slugs : []
        });
        setRegenScope(null);
    };

    const handleReindex = () => {
        void runPipeline(
            'reindex',
            'full index',
            docs.data?.length ?? 0,
            'index rebuild + cache invalidation',
            {}
        );
    };

    const draftMetaForDoc = (doc: HelpDocRow): LocalDocDraft =>
        localDrafts[doc.slug] ?? {
            title: doc.title,
            publishState: doc.publish_state ?? 'published',
            reviewNotes: doc.review_notes ?? '',
            bodyMarkdown: doc.body_markdown ?? ''
        };

    const savePreviewDraft = async (draft: LocalDocDraft) => {
        if (!previewDoc) return;
        try {
            await apiPost('/api/admin/help-docs/regenerate', {
                scope: 'single',
                slugs: [previewDoc.slug],
                operation: 'draft_save',
                editorial: {
                    slug: previewDoc.slug,
                    title: draft.title,
                    body_markdown: draft.bodyMarkdown,
                    publish_state: draft.publishState,
                    review_notes: draft.reviewNotes
                }
            });

            setLocalDrafts((prev) => ({ ...prev, [previewDoc.slug]: { ...draft } }));
            await Promise.all([
                qc.invalidateQueries({ queryKey: ['admin', 'help-docs', 'list'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'help-docs', 'kpis'] })
            ]);
            appendAuditEntry(qc, {
                actor: session.email,
                actor_role: session.role,
                action: 'help_docs.draft_save',
                target_type: 'help_doc',
                target_id: previewDoc.slug,
                severity: 'routine',
                reason: `Saved durable editorial state for ${previewDoc.slug}`
            });
            toast('Article draft saved', {
                kind: 'success',
                detail: `${draft.title} · ${draft.publishState}`
            });
            setPreviewDoc(null);
        } catch (error) {
            toast('Article draft save failed', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Unable to persist help doc draft')
            });
        }
    };

    return (
        <div className="stack-16">
            <header className="page-header">
                <div className="page-title-group">
                    <h1>Help docs</h1>
                    <p>Doc regeneration pipeline, search analytics, stale detection.</p>
                </div>
                <div className="page-actions">
                    <RoleButton
                        scope="support_ok"
                        className="btn btn-ghost"
                        onClick={handleReindex}
                        disabled={isRunning}
                        title={
                            isRunning ? 'A pipeline run is already active' : 'Rebuild search index'
                        }
                    >
                        <RefreshCw size={14} /> Rebuild index
                    </RoleButton>
                    <RoleButton
                        scope="owner_only"
                        className="btn btn-ghost"
                        onClick={() =>
                            setRegenScope({
                                kind: 'stale',
                                slugs: staleDocs.map((doc) => doc.slug),
                                total: staleCount
                            })
                        }
                        disabled={isRunning || staleCount === 0}
                    >
                        Regen stale {staleCount > 0 ? `(${staleCount})` : ''}
                    </RoleButton>
                    <RoleButton
                        scope="owner_only"
                        className="btn btn-primary"
                        onClick={() =>
                            setRegenScope({ kind: 'all', total: docs.data?.length ?? 0 })
                        }
                        disabled={isRunning}
                    >
                        Regenerate all
                    </RoleButton>
                </div>
            </header>

            <section className="alert-bar">
                <div className="alert" data-severity="info">
                    <span className="msg">
                        Inventory and query telemetry are live-backed; this revision adds preview,
                        publish-state, and Worker-backed regeneration/reindex audit actions.
                    </span>
                    <span className="meta">partial</span>
                </div>
            </section>

            <section className="kpi-grid kpi-grid-6" aria-label="Help docs metrics">
                <KpiMini label="Total docs" value={kpis.data?.total.toString() ?? '—'} />
                <KpiMini label="Last regen" value={kpis.data?.last_regen_at ?? '—'} />
                <KpiMini
                    label="Searches (30d)"
                    value={kpis.data?.searches_30d.toLocaleString() ?? '—'}
                />
                <KpiMini label="Most viewed" value={kpis.data?.most_viewed ?? '—'} />
                <KpiMini
                    label="Failed searches"
                    value={kpis.data ? `${kpis.data.failed_searches_pct.toFixed(1)}%` : '—'}
                    alert={!!kpis.data && kpis.data.failed_searches_pct > 10}
                />
                <KpiMini
                    label="Satisfaction"
                    value={kpis.data ? `${kpis.data.satisfaction_pct.toFixed(1)}%` : '—'}
                />
            </section>

            <div className="grid-split">
                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Docs inventory</h2>
                        <span className="text-muted">
                            {pipelineRun.lastStartedAt
                                ? `${pipelineRun.operation === 'none' ? PIPELINE_RUN_LABELS.none : PIPELINE_RUN_LABELS[pipelineRun.operation]} · ${pipelineRun.scopeLabel}`
                                : PIPELINE_RUN_LABELS.none}
                        </span>
                        {staleCount > 0 && <Chip tone="warning">{staleCount} stale</Chip>}
                    </header>
                    <div className="table-wrap">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Title</th>
                                    <th>Regenerated</th>
                                    <th className="numeric">Views 30d</th>
                                    <th className="numeric">Thumbs up</th>
                                    <th>Publish</th>
                                    <th>Status</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {docs.data && docs.data.length > 0 ? (
                                    docs.data.map((doc) => {
                                        const draft = draftMetaForDoc(doc);

                                        return (
                                            <tr key={doc.slug}>
                                                <td>
                                                    <div className="user-meta">
                                                        <span className="user-name">
                                                            {draft.title}
                                                        </span>
                                                        <span className="user-sub mono">
                                                            {doc.slug}
                                                        </span>
                                                        {doc.last_reviewed_by && (
                                                            <span className="sub">
                                                                reviewed by {doc.last_reviewed_by}
                                                                {doc.last_reviewed_at
                                                                    ? ` · ${doc.last_reviewed_at}`
                                                                    : ''}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="text-muted">
                                                    {doc.last_regenerated}
                                                </td>
                                                <td className="numeric">
                                                    {doc.views_30d.toLocaleString()}
                                                </td>
                                                <td
                                                    className="numeric"
                                                    style={{
                                                        color:
                                                            doc.thumbs_up_pct < 75
                                                                ? 'var(--warning)'
                                                                : 'var(--text-primary)'
                                                    }}
                                                >
                                                    {doc.thumbs_up_pct.toFixed(1)}%
                                                </td>
                                                <td>
                                                    <Chip
                                                        tone={publishStateTone[draft.publishState]}
                                                        dot
                                                    >
                                                        {publishStateLabel[draft.publishState]}
                                                    </Chip>
                                                </td>
                                                <td>
                                                    {doc.stale ? (
                                                        <Chip tone="warning" dot>
                                                            stale
                                                        </Chip>
                                                    ) : (
                                                        <Chip tone="success" dot>
                                                            fresh
                                                        </Chip>
                                                    )}
                                                </td>
                                                <td>
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            gap: 8,
                                                            justifyContent: 'flex-end'
                                                        }}
                                                    >
                                                        <button
                                                            className="btn btn-sm btn-ghost"
                                                            onClick={() => setPreviewDoc(doc)}
                                                        >
                                                            Preview
                                                        </button>
                                                        <RoleButton
                                                            scope="owner_only"
                                                            className="btn btn-sm btn-ghost"
                                                            disabled={isRunning}
                                                            onClick={() =>
                                                                setRegenScope({
                                                                    kind: 'single',
                                                                    slug: doc.slug,
                                                                    title: draft.title
                                                                })
                                                            }
                                                        >
                                                            Regen
                                                        </RoleButton>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={7} className="empty-state">
                                            No docs inventory is available yet.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>

                <div className="stack-12">
                    <section className="card">
                        <header className="card-header">
                            <h2 className="card-title">Pipeline status</h2>
                            <Chip tone={fmtPipelineChip(pipelineRun.status, pipelineRun.operation)}>
                                {pipelineRun.status === 'running'
                                    ? 'Running'
                                    : pipelineRun.status === 'failed'
                                      ? 'Failed'
                                      : pipelineRun.status === 'success'
                                        ? 'Last run complete'
                                        : 'Idle'}
                            </Chip>
                        </header>
                        <div className="card-body tight">
                            <dl className="kv-list">
                                <dt>Mode</dt>
                                <dd className="mono">
                                    {pipelineRun.operation === 'none'
                                        ? 'none'
                                        : PIPELINE_RUN_LABELS[pipelineRun.operation]}
                                </dd>
                                <dt>Scope</dt>
                                <dd>{pipelineRun.scopeLabel}</dd>
                                <dt>Docs affected</dt>
                                <dd className="mono">
                                    {pipelineRun.docsAffected.toLocaleString()}
                                </dd>
                                <dt>Last run</dt>
                                <dd>{pipelineRun.lastCompletedAt ?? '—'}</dd>
                                <dt>Duration</dt>
                                <dd>
                                    {pipelineRun.lastDurationMs
                                        ? `${(pipelineRun.lastDurationMs / 1000).toFixed(1)}s`
                                        : '—'}
                                </dd>
                                <dt>Index state</dt>
                                <dd className="text-muted">
                                    {pipelineRun.lastMessage ?? 'unknown'}
                                </dd>
                            </dl>
                            <div
                                style={{
                                    marginTop: 12,
                                    padding: 10,
                                    borderRadius: 6,
                                    background: 'var(--bg-input)',
                                    fontSize: 12,
                                    color: 'var(--text-muted)'
                                }}
                            >
                                {latestRun
                                    ? latestRunLabel(latestRun)
                                    : kpis.data?.provider
                                      ? `${kpis.data.provider} · fallback ${kpis.data.fallback_provider || 'n/a'}`
                                      : 'No durable run history yet'}
                            </div>
                        </div>
                    </section>

                    <section className="card">
                        <header className="card-header">
                            <h2 className="card-title">Top queries (30d)</h2>
                        </header>
                        <div className="card-body tight">
                            {top.data && top.data.length > 0 ? (
                                top.data.map((q) => (
                                    <QueryRow key={q.query} query={q.query} count={q.count} />
                                ))
                            ) : (
                                <div className="empty-state">
                                    <div className="title">Search query telemetry unavailable</div>
                                </div>
                            )}
                        </div>
                    </section>

                    <section className="card">
                        <header className="card-header">
                            <h2 className="card-title">Failed searches</h2>
                            <Chip tone="warning">gaps</Chip>
                        </header>
                        <div className="card-body tight">
                            {failed.data && failed.data.length > 0 ? (
                                failed.data.map((q) => (
                                    <QueryRow key={q.query} query={q.query} count={q.count} />
                                ))
                            ) : (
                                <div className="empty-state">
                                    <div className="title">No failed-query feed yet</div>
                                </div>
                            )}
                        </div>
                    </section>

                    <section className="card">
                        <header className="card-header">
                            <h2 className="card-title">Run history</h2>
                        </header>
                        <div className="card-body tight">
                            {kpis.data?.recent_runs && kpis.data.recent_runs.length > 0 ? (
                                kpis.data.recent_runs.map((run) => (
                                    <div
                                        key={run.id}
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr auto',
                                            gap: 8,
                                            padding: '10px 16px',
                                            borderBottom: '1px solid var(--border)',
                                            fontSize: 12
                                        }}
                                    >
                                        <div>
                                            <div style={{ color: 'var(--text-primary)' }}>
                                                {run.operation} · {run.scope}
                                            </div>
                                            <div
                                                style={{ fontSize: 11, color: 'var(--text-muted)' }}
                                            >
                                                {run.message}
                                                {run.actor_email ? ` · ${run.actor_email}` : ''}
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div className="mono">
                                                {(run.cost_cents / 100).toFixed(2)}
                                            </div>
                                            <div className="text-muted mono">
                                                {(run.duration_ms / 1000).toFixed(1)}s
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="empty-state">
                                    <div className="title">No durable run history yet</div>
                                </div>
                            )}
                        </div>
                    </section>
                </div>
            </div>

            <HelpDocsRegenConfirm
                scope={regenScope}
                onClose={() => setRegenScope(null)}
                onConfirm={handleConfirmRegen}
            />

            <HelpDocPreviewModal
                doc={previewDoc}
                draft={previewDoc ? draftMetaForDoc(previewDoc) : null}
                onClose={() => setPreviewDoc(null)}
                onSave={savePreviewDraft}
            />
        </div>
    );
}

function HelpDocPreviewModal({
    doc,
    draft,
    onClose,
    onSave
}: {
    doc: HelpDocRow | null;
    draft: LocalDocDraft | null;
    onClose: () => void;
    onSave: (draft: LocalDocDraft) => Promise<void> | void;
}) {
    const [title, setTitle] = useState('');
    const [publishState, setPublishState] = useState<PublishState>('published');
    const [reviewNotes, setReviewNotes] = useState('');
    const [bodyMarkdown, setBodyMarkdown] = useState('');

    useEffect(() => {
        if (!doc) return;

        setTitle(draft?.title ?? doc.title);
        setPublishState(draft?.publishState ?? 'published');
        setReviewNotes(draft?.reviewNotes ?? '');
        setBodyMarkdown(draft?.bodyMarkdown ?? doc.body_markdown ?? '');
    }, [doc, draft]);

    if (!doc) return null;

    const preview =
        `# ${title}\n\nSource slug: ${doc.slug}\n\n` +
        `Status: ${publishState}\n\n` +
        `Last regenerated: ${doc.last_regenerated}\n\n` +
        `Views 30d: ${doc.views_30d.toLocaleString()}\n` +
        `Thumbs up: ${doc.thumbs_up_pct.toFixed(1)}%\n`;

    return (
        <Modal
            open={!!doc}
            onClose={onClose}
            title={title}
            subtitle={`Local article workflow · ${doc.slug}`}
            width={680}
            footer={
                <>
                    <button className="btn btn-ghost" onClick={onClose}>
                        Close
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() =>
                            onSave({
                                title: title.trim() || doc.title,
                                publishState,
                                reviewNotes,
                                bodyMarkdown
                            })
                        }
                    >
                        <Save size={14} /> Save draft
                    </button>
                </>
            }
        >
            <dl className="kv-list">
                <dt>Publish state</dt>
                <dd>
                    <select
                        className="form-input"
                        value={publishState}
                        onChange={(event) => setPublishState(event.target.value as PublishState)}
                    >
                        <option value="published">published</option>
                        <option value="needs_review">needs review</option>
                        <option value="draft">draft</option>
                    </select>
                </dd>
                <dt>Review notes</dt>
                <dd>
                    <textarea
                        className="form-input"
                        rows={5}
                        style={{ width: '100%', resize: 'vertical' }}
                        placeholder="Comments for editorial review"
                        value={reviewNotes}
                        onChange={(event) => setReviewNotes(event.target.value)}
                    />
                </dd>
                <dt>Body markdown</dt>
                <dd>
                    <textarea
                        className="form-input"
                        rows={12}
                        style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
                        placeholder="Persisted markdown body for the article"
                        value={bodyMarkdown}
                        onChange={(event) => setBodyMarkdown(event.target.value)}
                    />
                </dd>
            </dl>

            <pre
                style={{
                    background: 'var(--bg-input)',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    padding: 10,
                    fontSize: 11,
                    fontFamily: 'JetBrains Mono, monospace',
                    color: 'var(--text-primary)',
                    whiteSpace: 'pre-wrap',
                    margin: '8px 0 0',
                    maxHeight: 220,
                    overflow: 'auto'
                }}
            >
                {preview}
            </pre>
        </Modal>
    );
}

function QueryRow({ query, count }: { query: string; count: number }) {
    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 10,
                padding: '10px 16px',
                borderBottom: '1px solid var(--border)',
                fontSize: 12
            }}
        >
            <span style={{ color: 'var(--text-primary)' }}>{query}</span>
            <span className="mono text-muted">{count}</span>
        </div>
    );
}

function KpiMini({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
    return (
        <div className="kpi-tile" data-alert={alert ? 'true' : 'false'}>
            <span className="label">{label}</span>
            <span className="value">{value}</span>
        </div>
    );
}
